import { useState, useMemo, useCallback } from 'react';

// Tipos genéricos para el hook
export interface VirtualizedGridConfig {
  itemsPerRow: number;
  itemHeight: number;
  containerHeight: number;
  searchFields: string[];
}

export interface VirtualizedGridFilters {
  [key: string]: string;
}

export interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  startIndex: number;
  endIndex: number;
}

export interface UseVirtualizedGridReturn<T> {
  // Datos filtrados y paginados
  visibleItems: T[];
  filteredItems: T[];
  totalFiltered: number;
  
  // Paginación
  pagination: PaginationInfo;
  
  // Filtros
  filters: VirtualizedGridFilters;
  hasActiveFilters: boolean;
  updateFilter: (field: string, value: string) => void;
  clearFilters: () => void;
  
  // Paginación
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  
  // Utilidades
  getFieldValue: (item: T, field: string) => string;
}

export const useVirtualizedGrid = <T extends Record<string, any>>(
  data: T[],
  config: VirtualizedGridConfig,
  initialFilters: VirtualizedGridFilters = {}
): UseVirtualizedGridReturn<T> => {
  
  const [filters, setFilters] = useState<VirtualizedGridFilters>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Calcular items por página basado en la configuración
  const itemsPerPage = config.itemsPerRow * Math.floor(config.containerHeight / config.itemHeight);

  // Función para obtener valor anidado de un objeto
  const getFieldValue = useCallback((item: T, field: string): string => {
    const keys = field.split('.');
    let value: any = item;
    
    for (const key of keys) {
      if (value && typeof value === 'object') {
        // Manejar arrays (ej: PropietarioPredio[0].Propietario.nombre)
        if (key.includes('[') && key.includes(']')) {
          const arrayKey = key.substring(0, key.indexOf('['));
          const indexMatch = key.match(/\[(\d+)\]/);
          const index = indexMatch ? parseInt(indexMatch[1]) : 0;
          value = value[arrayKey]?.[index];
        } else {
          value = value[key];
        }
      } else {
        return '';
      }
    }
    
    return String(value || '');
  }, []);

  // Filtrar datos
  const filteredItems = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.filter(item => {
      return Object.entries(filters).every(([filterKey, filterValue]) => {
        if (!filterValue) return true;
        
        // Si es un filtro de texto general, buscar en todos los campos configurados
        if (filterKey === 'texto' && config.searchFields) {
          return config.searchFields.some(field => {
            const fieldValue = getFieldValue(item, field);
            return fieldValue.toLowerCase().includes(filterValue.toLowerCase());
          });
        }
        
        // Filtro específico por campo
        const fieldValue = getFieldValue(item, filterKey);
        return fieldValue.toLowerCase().includes(filterValue.toLowerCase());
      });
    });
  }, [data, filters, config.searchFields, getFieldValue]);

  // Calcular paginación
  const pagination = useMemo((): PaginationInfo => {
    const totalItems = filteredItems.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

    return {
      currentPage: safePage,
      totalPages,
      totalItems,
      itemsPerPage,
      startIndex,
      endIndex
    };
  }, [filteredItems.length, itemsPerPage, currentPage]);

  // Obtener items visibles para la página actual
  const visibleItems = useMemo(() => {
    return filteredItems.slice(pagination.startIndex, pagination.endIndex);
  }, [filteredItems, pagination.startIndex, pagination.endIndex]);

  // Verificar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(value => value !== '');
  }, [filters]);

  // Funciones de control
  const updateFilter = useCallback((field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Resetear a la primera página al filtrar
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
    setCurrentPage(1);
  }, [initialFilters]);

  const goToPage = useCallback((page: number) => {
    const safePage = Math.max(1, Math.min(page, pagination.totalPages));
    setCurrentPage(safePage);
  }, [pagination.totalPages]);

  const nextPage = useCallback(() => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, pagination.totalPages]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  return {
    visibleItems,
    filteredItems,
    totalFiltered: filteredItems.length,
    pagination,
    filters,
    hasActiveFilters,
    updateFilter,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    getFieldValue
  };
};